import { MealCountDto, SponsorInfo, SiteInfo, MealCountCalcs, ServingLineSummary, ServingLine } from '@/models';
import { inject } from 'inversify-props';
import { Store } from 'vuex';
import { IState, types } from '@/store';
import moment from 'moment';
import { mcLabels, ServingLineViewModel } from '@/view-models'
import { IMealCountService } from '@/services';
import { mcDate, mcCounts, mcLogger } from '@/utils';

export class MealCountDtoViewModel {
  @inject() private store!: Store<IState>;
  @inject() private mealCountService!: IMealCountService;

  private console: Console = mcLogger.getConsole(this.constructor.name, mcLogger.colors.MealCountDtoViewModel);

  constructor(model: MealCountDto) {
    this.mealCounts = model;
  }

  private _mealCounts!: MealCountDto;
  get mealCounts() {
    return this._mealCounts;
  }

  set mealCounts(value) {
    this._mealCounts = value;
    this.tally = new MealCountCalcs(value);
  }

  get receivedMeals() {
    return this._mealCounts.receivedMeals;
  }

  set receivedMeals(value) {
    this._mealCounts.receivedMeals = value;
  }

  get userServingLineVm() {
    return new ServingLineViewModel(this.userServingLine || new ServingLine());
  }

  get userServingLine() {
    const userId = this.store.getters[types.state.user].userId;
    return mcCounts.findServingLine(this._mealCounts.servingLines, userId);
  }

  get nonUserServingLineVms() {
    let nonUserLines = this.nonUserServingLines;
    let result = new ServingLineViewModel[0];
    nonUserLines.forEach(m => result.push(new ServingLineViewModel(m)));
    return result;
  }

  get isNonCongregate() {
    return this.mealCounts.isNonCongregate;
  }

  get providedDays() {
    return this.mealCounts.providedDays;
  }

  /** Get the serving line information for everyone BUT the current user */
  get nonUserServingLines(): ServingLine[] {
    const userId = this.store.getters[types.state.user].userId;
    return mcCounts.findServingLinesForOthers(this._mealCounts.servingLines, userId) || [];
  }

  get servingLineSummary() {
    return new ServingLineSummary(this._mealCounts.servingLines);
  }

  get affirmed() {
    const notAffirmed = this._mealCounts.servingLines?.find(line => !line.affirmed) ?? false;
    return !notAffirmed;
  }

  public timezoneName: string = '';
  public timezoneOffset: number = 0;

  tally!: MealCountCalcs;

  get mealType() {
    return this.mealCounts.mealTypeId.replace('_', ' ').toUpperCase();
  }

  get siteName() {
    const siteId = this.mealCounts.siteId;
    const site: SiteInfo = this.store.getters[types.state.sites].find(s => s.siteId === siteId);

    const result = site ? site.siteName : '';
    return result;
  }

  get sponsorId() {
    const sponsor = this.sponsor;
    const result = sponsor ? sponsor.sponsorId : 0;
    return result;
  }

  get sponsorName() {
    const sponsor = this.sponsor;
    const result = sponsor ? sponsor.sponsorName : '';
    return result;
  }

  get sponsor(): SponsorInfo | null {
    let sponsor: SponsorInfo | null = null;

    const siteId = this.mealCounts.siteId;
    const site = this.store.getters[types.state.sites].find(s => s.siteId === siteId);

    if (site) {
      const sponsorId = site.sponsorId;
      sponsor = this.store.getters[types.state.sponsors].find(s => s.sponsorId === sponsorId);
    }
    return sponsor;
  }

  get serviceDate() {
    return moment(this.mealCounts.mealServiceDate).format('LL');
  }

  get deliveryTime() {
    return this.formatDate(this.mealCounts.receivedMeals.mealDeliveryDateTime, 'LLL');
  }
  set deliveryTime(value) {
    this.mealCounts.receivedMeals.mealDeliveryDateTime = mcDate.toUtc(value);
  }

  get syncDateUtc() {
    return this.formatDate(this.mealCounts.syncDateUtc, 'lll');
  }

  get syncDateLocal() {
    return moment.utc(<Date>this.mealCounts.syncDateUtc).local().format('LLL');
  }

  private formatDate(date, format) {
    if (!date) return '';
    return moment(date, moment.defaultFormatUtc).format(format);
  }

  /** checks if this dto matches sponsors/sites for the logged in user */
  get canShow() {
    return this.sponsor !== null;
  }

  get isSynced() {
    return this.mealCountService.syncedMeal(this.mealCounts);
  }

  get isUnsynced() {
    return this.mealCountService.unsyncedMeal(this.mealCounts);
  }

  get summary() {
    const l = mcLabels.mealCounts;
    const servingLineSummary = this.servingLineSummary;
    if (this.mealCounts.isNonCongregate) {
      return [
        { label: l.program, value: this.mealCounts.programId },
        { label: l.sponsor, value: this.sponsorName },
        { label: l.site, value: this.siteName, class: 'total-row' },
        { label: l.providedDays, value: this.providedDays },
        { label: l.receive.receivedTime, value: this.deliveryTime },
        { label: l.receive.receivedTemp, value: this.receivedMeals.mealDeliveryTemperature },
        { label: l.receive.receivedMeals, value: this.receivedMeals.mealCountReceived },
        { label: l.receive.previousLeftoverMeals, value: this.receivedMeals.mealCountLeftoverPrevious },
        { label: l.receive.totalMealsAvailable, value: this.tally.availableCount, class: 'total-row' },
        { label: l.serve.nonCongregateMeals, value: servingLineSummary.nonCongregateMeal },
        { label: l.serve.totalMealsServed, value: this.tally.totalCount, class: 'total-row' },
        { label: l.waste.mealsWasted, value: servingLineSummary.wastedMeal },
        { label: l.waste.mealsIncomplete, value: servingLineSummary.incompleteMeal },
        { label: l.waste.totalMealsLeftover, value: this.tally.newLeftovers, class: 'total-row' },
      ];
    } else {
      return [
        { label: l.program, value: this.mealCounts.programId },
        { label: l.sponsor, value: this.sponsorName },
        { label: l.site, value: this.siteName, class: 'total-row' },
        { label: l.receive.receivedTime, value: this.deliveryTime },
        { label: l.receive.receivedTemp, value: this.receivedMeals.mealDeliveryTemperature },
        { label: l.receive.receivedMeals, value: this.receivedMeals.mealCountReceived },
        { label: l.receive.previousLeftoverMeals, value: this.receivedMeals.mealCountLeftoverPrevious },
        { label: l.receive.totalMealsAvailable, value: this.tally.availableCount, class: 'total-row' },
        { label: l.serve.firstMeals, value: servingLineSummary.firstMeal },
        { label: l.serve.secondMeals, value: servingLineSummary.secondMeal },
        { label: l.serve.adultProgramMeals, value: servingLineSummary.adultProgramMeal },
        { label: l.serve.adultNonProgramMeals, value: servingLineSummary.adultNonprogramMeal },
        { label: l.serve.additionalMealsRequested, value: servingLineSummary.additionalMealsRequested },
        { label: l.serve.totalMealsServed, value: this.tally.totalCount, class: 'total-row' },
        { label: l.waste.mealsWasted, value: servingLineSummary.wastedMeal },
        { label: l.waste.mealsIncomplete, value: servingLineSummary.incompleteMeal },
        { label: l.waste.totalMealsLeftover, value: this.tally.newLeftovers, class: 'total-row' },
      ];
    }
  }

  get syncSummary() {
    const l = mcLabels.mealCounts;
    return [...this.summary,
      { label: l.affirmed, value: this.affirmed }
    ];
  }
}
